import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getVRApuracao, fecharVRApuracao } from '../services/api';
import jsPDF from 'jspdf';

const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const fmtMes = (m, a) => m && a ? `${MESES_PT[m-1]}/${a}` : '—';
const fmtNum = n => n == null ? '—' : (Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(1));

const COR = {
  normal:             { bg: '#fff',     text: '#1e293b' },
  falta:              { bg: '#fca5a5', text: '#7f1d1d' },
  afastamento:        { bg: '#fed7aa', text: '#7c2d12' },
  ferias:             { bg: '#bfdbfe', text: '#1e3a8a' },
  atestado:           { bg: '#fef08a', text: '#713f12' },
  folga:              { bg: '#e5e7eb', text: '#6b7280' },
  feriado:            { bg: '#d1fae5', text: '#065f46' },
  feriado_trabalhado: { bg: '#a7f3d0', text: '#065f46' },
};
const LABEL = { normal:'1', falta:'F', afastamento:'A', ferias:'F', atestado:'AT', folga:'', feriado:'Fe', feriado_trabalhado:'+1' };

// ── Exportações ──────────────────────────────────────────────────────────────

function exportarCSV(vr) {
  const label = `${MESES_PT[vr.mes_cred-1]}_${vr.ano_cred}`;
  const header = 'Funcionário;Dias Úteis;Férias;Afastam.;Base VR;Faltas;F.Just.<6h;Extras;Total VR\n';
  const rows = vr.itens.map(i =>
    [i.nome, i.diasUteis, i.diasFerias||0, i.diasAfastamento||0, i.vrBase,
     i.faltas, i.faltasJust,
     (i.extrasInteiro + i.extrasMeio * 0.5).toFixed(1),
     Number(i.total).toFixed(1)
    ].join(';')
  ).join('\n');
  const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `VR_${label}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function exportarExcel(vr) {
  const label = `${MESES_PT[vr.mes_cred-1]}_${vr.ano_cred}`;
  const header = ['Funcionário','Dias Úteis','Férias','Afastam.','Base VR','Faltas','F.Just.<6h','Extras','Total VR'].join('\t') + '\n';
  const rows = vr.itens.map(i =>
    [i.nome, i.diasUteis, i.diasFerias||0, i.diasAfastamento||0, i.vrBase,
     i.faltas, i.faltasJust,
     (i.extrasInteiro + i.extrasMeio * 0.5).toFixed(1),
     Number(i.total).toFixed(1)
    ].join('\t')
  ).join('\n');
  const blob = new Blob(['\uFEFF' + header + rows], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `VR_${label}.xls`; a.click();
  URL.revokeObjectURL(url);
}

function exportarPDF(vr) {
  const labelCred = fmtMes(vr.mes_cred, vr.ano_cred);
  const labelRef  = fmtMes(vr.mes_ref,  vr.ano_ref);
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pw = doc.internal.pageSize.getWidth();
  const ML = 14, MR = 14;

  // Cabeçalho
  doc.setFontSize(13); doc.setTextColor(79, 70, 229);
  doc.text(`Apuração de VR — Competência ${labelCred}`, ML, 16);
  doc.setFontSize(8.5); doc.setTextColor(100, 116, 139);
  doc.text(`Mês Atual: ${labelRef}  ·  Status: ${vr.status}  ·  Gerado em ${new Date().toLocaleDateString('pt-BR')}`, ML, 22);
  doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.3); doc.line(ML, 25, pw - MR, 25);

  // Colunas
  const cols = [
    { label: 'Funcionário',  w: 58 },
    { label: 'Dias Úteis',   w: 17 },
    { label: 'Férias',       w: 16 },
    { label: 'Afastam.',     w: 16 },
    { label: 'Base VR',      w: 17 },
    { label: 'Faltas',       w: 15 },
    { label: 'F.Just.<6h',   w: 17 },
    { label: 'Extras',       w: 15 },
    { label: 'Total VR',     w: 17 },
  ];
  const totalW = cols.reduce((s,c) => s+c.w, 0);
  const tableX = ML;
  let y = 30;
  const rowH = 7;

  // Header row
  doc.setFillColor(26, 46, 74); doc.rect(tableX, y, totalW, rowH, 'F');
  doc.setFontSize(7); doc.setTextColor(147, 197, 253); doc.setFont('helvetica', 'bold');
  let cx = tableX;
  cols.forEach(c => {
    doc.text(c.label, cx + c.w/2, y + 4.5, { align: 'center' });
    cx += c.w;
  });
  y += rowH;

  // Data rows
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
  vr.itens.forEach((item, idx) => {
    if (idx % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(tableX, y, totalW, rowH, 'F'); }
    doc.setTextColor(30, 41, 59);
    const vals = [
      item.nome,
      String(item.diasUteis),
      (item.diasFerias||0) > 0 ? String(item.diasFerias) : '—',
      (item.diasAfastamento||0) > 0 ? String(item.diasAfastamento) : '—',
      String(item.vrBase),
      item.faltas > 0 ? String(item.faltas) : '—',
      item.faltasJust > 0 ? fmtNum(item.faltasJust) : '—',
      (item.extrasInteiro + item.extrasMeio * 0.5) > 0 ? `+${fmtNum(item.extrasInteiro + item.extrasMeio * 0.5)}` : '—',
      fmtNum(Number(item.total)),
    ];
    cx = tableX;
    vals.forEach((v, vi) => {
      const align = vi === 0 ? 'left' : 'center';
      const x2 = vi === 0 ? cx + 2 : cx + cols[vi].w/2;
      if (vi === vals.length - 1) { doc.setTextColor(79, 70, 229); doc.setFont('helvetica', 'bold'); }
      doc.text(v, x2, y + 4.5, { align });
      doc.setTextColor(30, 41, 59); doc.setFont('helvetica', 'normal');
      cx += cols[vi].w;
    });
    doc.setDrawColor(226, 232, 240); doc.setLineWidth(0.2); doc.line(tableX, y + rowH, tableX + totalW, y + rowH);
    y += rowH;
  });

  // Totais
  const tot = vr.itens.reduce((acc, x) => ({
    diasUteis: acc.diasUteis + x.diasUteis,
    diasSemDireito: acc.diasSemDireito + x.diasSemDireito,
    vrBase: acc.vrBase + x.vrBase,
    faltas: acc.faltas + x.faltas,
    faltasJust: acc.faltasJust + x.faltasJust,
    extrasInteiro: acc.extrasInteiro + x.extrasInteiro,
    extrasMeio: acc.extrasMeio + x.extrasMeio,
    total: acc.total + Number(x.total),
  }), { diasUteis:0,diasSemDireito:0,vrBase:0,faltas:0,faltasJust:0,extrasInteiro:0,extrasMeio:0,total:0 });

  doc.setFillColor(239, 246, 255); doc.rect(tableX, y, totalW, rowH, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
  const totVals = [
    `${vr.itens.length} func.`,
    String(tot.diasUteis), tot.diasSemDireito > 0 ? String(tot.diasSemDireito) : '—',
    String(tot.vrBase),
    tot.faltas > 0 ? `−${tot.faltas}` : '—',
    tot.faltasJust > 0 ? `−${fmtNum(tot.faltasJust*0.5)}` : '—',
    (tot.extrasInteiro+tot.extrasMeio*0.5)>0 ? `+${fmtNum(tot.extrasInteiro+tot.extrasMeio*0.5)}` : '—',
    fmtNum(tot.total),
  ];
  cx = tableX;
  totVals.forEach((v, vi) => {
    if (vi === totVals.length-1) doc.setTextColor(79, 70, 229);
    else doc.setTextColor(30, 41, 59);
    const align = vi === 0 ? 'left' : 'center';
    const x2 = vi === 0 ? cx+2 : cx+cols[vi].w/2;
    doc.text(v, x2, y+4.5, { align });
    cx += cols[vi].w;
  });

  doc.save(`VR_${labelCred.replace('/','-')}.pdf`);
}

// ── Componente ───────────────────────────────────────────────────────────────

export default function VRDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vr, setVr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [tab, setTab] = useState('resumo');
  const [fechando, setFechando] = useState(false);
  const [erroFechar, setErroFechar] = useState('');
  const [confirmarFechar, setConfirmarFechar] = useState(false);

  useEffect(() => {
    getVRApuracao(id)
      .then(r => setVr(r.data))
      .catch(() => setErro('Erro ao carregar VR.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleFechar = async () => {
    setFechando(true); setErroFechar('');
    try {
      const r = await fecharVRApuracao(id);
      setVr(r.data);
      setConfirmarFechar(false);
    } catch (e) {
      setErroFechar(e.response?.data?.erro || 'Erro ao fechar VR.');
    } finally { setFechando(false); }
  };

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', padding:'60px 0', color:'var(--text-muted)' }}>
      <span className="spinner-border spinner-border-sm me-2" />Carregando VR...
    </div>
  );
  if (erro) return <div className="alert-inline alert-inline-danger"><i className="bi bi-exclamation-triangle" />{erro}</div>;
  if (!vr) return null;

  const labelCred = fmtMes(vr.mes_cred, vr.ano_cred);
  const labelRef  = fmtMes(vr.mes_ref,  vr.ano_ref);
  const isFechado = vr.status === 'FECHADO';

  const tot = (vr.itens || []).reduce((acc, x) => ({
    diasUteis:       acc.diasUteis       + (x.diasUteis||0),
    diasFerias:      acc.diasFerias      + (x.diasFerias||0),
    diasAfastamento: acc.diasAfastamento + (x.diasAfastamento||0),
    vrBase:          acc.vrBase          + (x.vrBase||0),
    faltas:          acc.faltas          + (x.faltas||0),
    faltasJust:      acc.faltasJust      + (x.faltasJust||0),
    extrasInteiro:   acc.extrasInteiro   + (x.extrasInteiro||0),
    extrasMeio:      acc.extrasMeio      + (x.extrasMeio||0),
    total:           acc.total           + Number(x.total||0),
  }), { diasUteis:0,diasFerias:0,diasAfastamento:0,vrBase:0,faltas:0,faltasJust:0,extrasInteiro:0,extrasMeio:0,total:0 });

  const tabs = [
    { key:'referencia',  icon:'bi-calendar-minus', label:'Mês Atual',  sub: labelRef  },
    { key:'competencia', icon:'bi-calendar-check',  label:'Competência', sub: labelCred },
    { key:'resumo',      icon:'bi-receipt',         label:'Resumo',      sub: 'Cálculo final' },
  ];

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', paddingBottom:16, marginBottom:20, borderBottom:'1px solid #f1f5f9' }}>
        <div style={{ display:'flex', alignItems:'center' }}>
          <div style={{ width:38, height:38, background:'#faf5ff', border:'1px solid #d8b4fe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed', fontSize:'1rem', marginRight:14, flexShrink:0 }}>
            <i className="bi bi-receipt" />
          </div>
          <div>
            <div style={{ fontSize:'var(--fs-2xs)', fontWeight:600, color:'var(--text-subtle)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 }}>Gestão de Pessoal · Controle de Ponto</div>
            <div style={{ fontSize:'1.3rem', fontWeight:700, color:'#334155', letterSpacing:'-0.5px', lineHeight:1.2 }}>
              VR #{vr.id} — Competência {labelCred}
              <span style={{ marginLeft:10, fontSize:'var(--fs-xs)', fontWeight:700, borderRadius:999, padding:'2px 10px',
                background: isFechado ? '#f0fdf4' : '#eff6ff',
                color:      isFechado ? '#15803d' : '#1d4ed8',
                border:`1px solid ${isFechado ? '#bbf7d0':'#bfdbfe'}`,
              }}>{isFechado ? 'Fechado' : 'Aberto'}</span>
            </div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {/* Export (só quando fechado) */}
          {isFechado && (
            <>
              <button className="btn-secondary-custom" style={{ color:'#15803d', borderColor:'#86efac' }} onClick={() => exportarCSV(vr)}>
                <i className="bi bi-filetype-csv" /> CSV
              </button>
              <button className="btn-secondary-custom" style={{ color:'#166534', borderColor:'#bbf7d0' }} onClick={() => exportarExcel(vr)}>
                <i className="bi bi-file-earmark-excel" /> Excel
              </button>
              <button className="btn-secondary-custom" style={{ color:'#dc2626', borderColor:'#fecaca' }} onClick={() => exportarPDF(vr)}>
                <i className="bi bi-file-earmark-pdf" /> PDF
              </button>
            </>
          )}
          {/* Fechar VR */}
          {!isFechado && !confirmarFechar && (
            <button className="btn-danger-custom" onClick={() => setConfirmarFechar(true)}>
              <i className="bi bi-lock-fill" /> Fechar VR
            </button>
          )}
          <button className="btn-secondary-custom" onClick={() => navigate('/relatorio-vr')}>
            <i className="bi bi-arrow-left" /> Voltar
          </button>
        </div>
      </div>

      {/* Confirmação de fechar */}
      {confirmarFechar && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontWeight:700, color:'#991b1b', fontSize:'var(--fs-sm)' }}>
              <i className="bi bi-exclamation-triangle me-2" />Confirmar fechamento do VR?
            </div>
            <div style={{ fontSize:'var(--fs-xs)', color:'var(--text-muted)', marginTop:2 }}>
              Após fechado, o VR não poderá ser reaberto. Os exports estarão disponíveis.
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn-secondary-custom" onClick={() => setConfirmarFechar(false)}>Cancelar</button>
            <button className="btn-danger-custom" onClick={handleFechar} disabled={fechando}>
              {fechando ? <span className="spinner-border spinner-border-sm" style={{width:10,height:10}} /> : <i className="bi bi-lock-fill" />}
              {fechando ? ' Fechando...' : ' Confirmar'}
            </button>
          </div>
        </div>
      )}
      {erroFechar && (
        <div style={{ marginBottom:12, fontSize:'var(--fs-xs)', color:'#dc2626' }}>
          <i className="bi bi-exclamation-circle me-1" />{erroFechar}
        </div>
      )}

      {/* Cards de resumo */}
      <div style={{ display:'grid', gridTemplateColumns: isFechado ? 'repeat(4,1fr)' : 'repeat(3,1fr)', gap:12, marginBottom:20 }}>
        {/* Mês Atual */}
        <div style={{ background:'#fff', border:'1px solid #fde68a', borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, boxShadow:'0 1px 4px rgba(234,179,8,0.08)', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:'linear-gradient(180deg,#fbbf24,#f59e0b)', borderRadius:'10px 0 0 10px' }} />
          <div style={{ width:40, height:40, background:'#fef9c3', border:'1px solid #fde68a', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#b45309', flexShrink:0, fontSize:'1.1rem' }}>
            <i className="bi bi-calendar-minus" />
          </div>
          <div>
            <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#92400e', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>Mês Atual</div>
            <div style={{ fontSize:'1.05rem', fontWeight:700, color:'#1e293b', letterSpacing:'-0.3px' }}>{labelRef}</div>
          </div>
        </div>

        {/* Competência */}
        <div style={{ background:'#fff', border:'1px solid #bfdbfe', borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, boxShadow:'0 1px 4px rgba(37,99,235,0.07)', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:'linear-gradient(180deg,#60a5fa,#2563eb)', borderRadius:'10px 0 0 10px' }} />
          <div style={{ width:40, height:40, background:'#dbeafe', border:'1px solid #bfdbfe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#1d4ed8', flexShrink:0, fontSize:'1.1rem' }}>
            <i className="bi bi-calendar-check" />
          </div>
          <div>
            <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#1d4ed8', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>Competência (base)</div>
            <div style={{ fontSize:'1.05rem', fontWeight:700, color:'#1e293b', letterSpacing:'-0.3px' }}>{labelCred}</div>
          </div>
        </div>

        {/* Total Tickets */}
        <div style={{ background:'#fff', border:'1px solid #d8b4fe', borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, boxShadow:'0 1px 4px rgba(124,58,237,0.08)', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:'linear-gradient(180deg,#a78bfa,#7c3aed)', borderRadius:'10px 0 0 10px' }} />
          <div style={{ width:40, height:40, background:'#ede9fe', border:'1px solid #d8b4fe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed', flexShrink:0, fontSize:'1.1rem' }}>
            <i className="bi bi-ticket-perforated" />
          </div>
          <div>
            <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:2 }}>Total Tickets</div>
            <div style={{ fontSize:'1.6rem', fontWeight:800, color:'#4f46e5', lineHeight:1, letterSpacing:'-0.5px' }}>{fmtNum(vr.total_tickets)}</div>
            <div style={{ fontSize:'var(--fs-2xs)', color:'var(--text-muted)', marginTop:3 }}>{vr.total_funcionarios} funcionário(s)</div>
          </div>
        </div>

        {/* Fechado em */}
        {isFechado && (
          <div style={{ background:'#fff', border:'1px solid #bbf7d0', borderRadius:10, padding:'14px 18px', display:'flex', alignItems:'center', gap:14, boxShadow:'0 1px 4px rgba(21,128,61,0.07)', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, width:4, height:'100%', background:'linear-gradient(180deg,#4ade80,#16a34a)', borderRadius:'10px 0 0 10px' }} />
            <div style={{ width:40, height:40, background:'#dcfce7', border:'1px solid #bbf7d0', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', color:'#15803d', flexShrink:0, fontSize:'1.1rem' }}>
              <i className="bi bi-lock-fill" />
            </div>
            <div>
              <div style={{ fontSize:'var(--fs-2xs)', fontWeight:700, color:'#15803d', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>Fechado em</div>
              <div style={{ fontSize:'var(--fs-base)', fontWeight:700, color:'#1e293b' }}>{vr.fechado_em_fmt}</div>
            </div>
          </div>
        )}
      </div>

      {/* Abas */}
      <div style={{ display:'flex', gap:6, marginBottom:16 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px',
            borderRadius:8, fontWeight:600, fontSize:'var(--fs-xs)', cursor:'pointer',
            border: tab===t.key ? '2px solid #d8b4fe' : '1px solid #e2e8f0',
            background: tab===t.key ? '#7c3aed' : '#f8fafc',
            color: tab===t.key ? '#fff' : '#475569',
            transition: 'all 0.15s',
          }}>
            <i className={`bi ${t.icon}`} />{t.label}
            <span style={{ fontSize:'var(--fs-2xs)', opacity:0.75, marginLeft:2 }}>({t.sub})</span>
          </button>
        ))}
      </div>

      {/* Conteúdo das abas */}
      {(tab === 'referencia' || tab === 'competencia') && (() => {
        const dados = tab === 'referencia' ? vr.dados_ref : vr.dados_cred;
        const labelTab = tab === 'referencia' ? labelRef : labelCred;
        if (!dados || !dados.funcionarios) return <div className="empty-state"><i className="bi bi-exclamation-circle" /><p>Dados não disponíveis.</p></div>;
        const diasList = dados.dias || [];
        return (
          <div className="card-ponto p-0">
            <div style={{ padding:'10px 16px', borderBottom:'1px solid #f1f5f9', background:'#f8fafc', fontSize:'var(--fs-xs)', color:'var(--text-muted)', display:'flex', flexWrap:'wrap', gap:12 }}>
              {Object.entries(COR).map(([tipo, cor]) => (
                <span key={tipo} style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                  <span style={{ width:16, height:16, background:cor.bg, border:'1px solid #d1d5db', borderRadius:2, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.55rem', fontWeight:700, color:cor.text }}>
                    {LABEL[tipo]}
                  </span>
                  {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                </span>
              ))}
              <span style={{ marginLeft:'auto', fontWeight:600, color:'#1e293b' }}>{labelTab}</span>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', fontSize:'var(--fs-xs)', width:'100%', tableLayout:'fixed' }}>
                <colgroup>
                  <col style={{ width:200 }} />
                  {diasList.map(d => <col key={d} style={{ width:22 }} />)}
                  <col style={{ width:46 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ position:'sticky', left:0, zIndex:2, background:'#1a2e4a', color:'#fff', padding:'6px 8px 6px 24px', fontWeight:700, borderRight:'1px solid #334', fontSize:'var(--fs-xs)', whiteSpace:'nowrap', verticalAlign:'bottom' }}>
                      Nome do Funcionário
                    </th>
                    {diasList.map(d => {
                      const dt = new Date(d + 'T00:00:00');
                      const dia = String(dt.getDate()).padStart(2,'0');
                      const mes2 = String(dt.getMonth()+1).padStart(2,'0');
                      const diaSemana = dt.getDay();
                      const semana = ['dom','seg','ter','qua','qui','sex','sáb'][diaSemana];
                      const isDom = diaSemana === 0;
                      return (
                        <th key={d} style={{ background: isDom ? '#7f1d1d' : '#1a2e4a', color:'#fff', padding:'4px 2px', textAlign:'center', borderRight:'1px solid #2d4060', verticalAlign:'bottom' }}>
                          <div style={{ writingMode:'vertical-rl', transform:'rotate(180deg)', textAlign:'left', whiteSpace:'nowrap', fontSize:'var(--fs-2xs)', padding:'4px 2px', fontWeight:600 }}>
                            {`${dia}/${mes2}`}<br />
                            <span style={{ fontWeight:700, opacity: isDom ? 1 : 0.8 }}>{semana}</span>
                          </div>
                        </th>
                      );
                    })}
                    <th style={{ background:'#1a2e4a', color:'#93c5fd', padding:'6px 4px', textAlign:'center', fontWeight:700, fontSize:'var(--fs-xs)', whiteSpace:'nowrap', verticalAlign:'bottom' }}>
                      TOTAL
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {dados.funcionarios.map((func, idx) => {
                    const SIGLA_MAP = ['DOM','SEG','TER','QUA','QUI','SEX','SAB'];
                    const diasSemanaFunc = new Set(func.dias_semana || ['SEG','TER','QUA','QUI','SEX']);
                    const cargaH = Number(func.carga_horaria) || 8;
                    const labelExtra = cargaH < 6 ? '+0,5' : '+1';

                    // TOTAL da coluna
                    const item = (vr.itens || []).find(x => x.id === func.id);
                    let totalCelula, totalCor;
                    if (tab === 'referencia') {
                      const saldo = item ? (item.acrescimoTotal - item.descontoTotal) : 0;
                      totalCelula = saldo === 0 ? '—' : saldo > 0 ? `+${saldo % 1 === 0 ? saldo : saldo.toFixed(1)}` : String(saldo % 1 === 0 ? saldo : saldo.toFixed(1));
                      totalCor = saldo > 0 ? '#7c3aed' : saldo < 0 ? '#dc2626' : '#94a3b8';
                    } else {
                      const vrBase = item ? item.vrBase : 0;
                      totalCelula = Number(vrBase).toFixed(2).replace('.', ',');
                      totalCor = '#1d4ed8';
                    }

                    return (
                    <tr key={func.id} style={{ background: idx%2===0 ? '#fff' : '#f8fafc' }}>
                      <td style={{ position:'sticky', left:0, zIndex:1, background: idx%2===0 ? '#fff' : '#f8fafc', padding:'4px 8px 4px 24px', fontWeight:500, borderRight:'1px solid #e2e8f0', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:200, fontSize:'var(--fs-xs)' }}>
                        {func.nome}
                      </td>
                      {diasList.map(d => {
                        const info = func.dias && func.dias[d];
                        const isDomCell = new Date(d + 'T00:00:00').getDay() === 0;
                        if (!info || info.valor === null) return <td key={d} style={{ background: isDomCell ? '#fecaca' : '#f9fafb', border:'1px solid #e5e7eb', padding:0, width:22 }} />;
                        const cor = COR[info.tipo] || COR.folga;
                        const siglaD = SIGLA_MAP[new Date(d + 'T00:00:00').getDay()];
                        let cellLabel = LABEL[info.tipo] ?? '';
                        if (tab === 'referencia') {
                          if (info.tipo === 'normal') {
                            cellLabel = diasSemanaFunc.has(siglaD) ? '' : labelExtra;
                          } else if (info.tipo === 'falta') {
                            cellLabel = '-1';
                          } else if (info.tipo === 'feriado_trabalhado') {
                            cellLabel = labelExtra;
                          }
                        } else if (tab === 'competencia') {
                          if (info.tipo === 'normal' && siglaD === 'SAB' && diasSemanaFunc.has('SAB')) {
                            cellLabel = '0,5';
                          } else if (info.tipo === 'normal') {
                            cellLabel = '1';
                          }
                        }
                        return (
                          <td key={d} style={{ background:cor.bg, color:cor.text, border:'1px solid #e5e7eb', textAlign:'center', fontWeight:600, padding:'2px 1px', fontSize:'var(--fs-2xs)', width:22 }}>
                            {cellLabel}
                          </td>
                        );
                      })}
                      <td style={{ textAlign:'center', fontWeight:700, color:totalCor, padding:'4px 6px', fontSize:'var(--fs-sm)', borderLeft:'2px solid #bfdbfe' }}>
                        {totalCelula}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {tab === 'resumo' && (
        <div className="card-ponto p-0">
          <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 20px', borderBottom:'1px solid #f1f5f9', background:'#faf5ff' }}>
            <div style={{ width:30, height:30, background:'#ede9fe', border:'1px solid #d8b4fe', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#7c3aed' }}>
              <i className="bi bi-receipt" />
            </div>
            <div style={{ fontWeight:700, fontSize:'var(--fs-base)', color:'#1e293b' }}>Resumo Final</div>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table className="table-ponto" style={{ width:'100%' }}>
              <thead>
                <tr>
                  {[
                    ['Funcionário', '#fff', false, 28],
                    ['Dias Úteis', '#93c5fd', true],
                    ['Férias', '#bfdbfe', true],
                    ['Afastam.', '#fed7aa', true],
                    ['Base VR', '#6ee7b7', true],
                    ['Faltas', '#fca5a5', true],
                    ['F.Just.<6h', '#fde68a', true],
                    ['Extras', '#c4b5fd', true],
                    ['Total VR', '#fbbf24', true],
                  ].map(([label, color, center, pl]) => (
                    <th key={label} style={{ padding:'9px 10px', ...(pl ? { paddingLeft:pl } : {}), textAlign: center ? 'center' : 'left', fontWeight:700, color, background:'#1a2e4a', borderRight:'1px solid #2d4060', fontSize:'var(--fs-2xs)', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(vr.itens || []).map((item, i) => (
                  <tr key={item.id || i} style={{ background: i%2===0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ paddingLeft:28, fontWeight:600, color:'#1e293b', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:240, borderRight:'1px solid #e2e8f0' }}>{item.nome}</td>
                    <td style={{ textAlign:'center', color:'var(--text-secondary)', borderRight:'1px solid #e2e8f0' }}>{item.diasUteis}</td>
                    <td style={{ textAlign:'center', borderRight:'1px solid #e2e8f0' }}>
                      {(item.diasFerias||0) > 0 ? <span style={{ background:'#dbeafe', color:'#1d4ed8', borderRadius:4, padding:'1px 8px', fontWeight:700 }}>{item.diasFerias}</span> : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ textAlign:'center', borderRight:'1px solid #e2e8f0' }}>
                      {(item.diasAfastamento||0) > 0 ? <span style={{ background:'#ffedd5', color:'#c2410c', borderRadius:4, padding:'1px 8px', fontWeight:700 }}>{item.diasAfastamento}</span> : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ textAlign:'center', fontWeight:700, color:'#059669', borderRight:'2px solid #d1d5db' }}>{item.vrBase}</td>
                    <td style={{ textAlign:'center', borderRight:'1px solid #e2e8f0' }}>
                      {item.faltas > 0 ? <span style={{ background:'#fee2e2', color:'#dc2626', borderRadius:4, padding:'1px 8px', fontWeight:700 }}>−{item.faltas}</span> : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ textAlign:'center', borderRight:'1px solid #e2e8f0' }}>
                      {item.faltasJust > 0 ? <span style={{ background:'#fef9c3', color:'#92400e', borderRadius:4, padding:'1px 8px', fontWeight:700 }}>−{fmtNum(item.faltasJust * 0.5)}</span> : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ textAlign:'center', borderRight:'2px solid #d1d5db' }}>
                      {(item.extrasInteiro + item.extrasMeio) > 0
                        ? <span style={{ background:'#ede9fe', color:'#7c3aed', borderRadius:4, padding:'1px 8px', fontWeight:700 }}>
                            +{fmtNum(item.extrasInteiro + item.extrasMeio * 0.5)}
                          </span>
                        : <span style={{ color:'#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ textAlign:'center', fontWeight:800, color:'#4f46e5', fontSize:'var(--fs-base)' }}>{fmtNum(Number(item.total))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background:'#eff6ff', borderTop:'2px solid #bfdbfe' }}>
                  <td style={{ paddingLeft:28, fontWeight:700, color:'#1e293b', borderRight:'1px solid #bfdbfe', fontSize:'var(--fs-xs)' }}>{(vr.itens||[]).length} func.</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'var(--text-secondary)', borderRight:'1px solid #bfdbfe' }}>{tot.diasUteis}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#1d4ed8', borderRight:'1px solid #bfdbfe' }}>{tot.diasFerias > 0 ? tot.diasFerias : '—'}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#c2410c', borderRight:'1px solid #bfdbfe' }}>{tot.diasAfastamento > 0 ? tot.diasAfastamento : '—'}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#059669', borderRight:'2px solid #bfdbfe' }}>{tot.vrBase}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#dc2626', borderRight:'1px solid #bfdbfe' }}>{tot.faltas > 0 ? `−${tot.faltas}` : '—'}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#92400e', borderRight:'1px solid #bfdbfe' }}>{tot.faltasJust > 0 ? `−${fmtNum(tot.faltasJust*0.5)}` : '—'}</td>
                  <td style={{ textAlign:'center', fontWeight:700, color:'#7c3aed', borderRight:'2px solid #bfdbfe' }}>{(tot.extrasInteiro+tot.extrasMeio)>0 ? `+${fmtNum(tot.extrasInteiro+tot.extrasMeio*0.5)}` : '—'}</td>
                  <td style={{ textAlign:'center', fontWeight:800, color:'#4f46e5', fontSize:'var(--fs-md)' }}>{fmtNum(tot.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
